/**
 * Mises en relation — le métier du closer, vu des trois côtés.
 *
 * Un même écran, trois onglets, parce que dans la vraie vie on est
 * successivement les trois : celui qui présente, celui qu'on présente, celui à
 * qui on présente. Ce qui change d'un onglet à l'autre, c'est ce qu'on a le
 * droit de voir — l'acheteur ne découvre l'entreprise qu'après avoir débloqué.
 */
import { useCallback, useEffect, useState } from 'react';
import { ArrowUpRight, Building2, Calendar, Check, GraduationCap, Lock, Star, X } from 'lucide-react';
import { appelerApi, envoyer } from '../../reseau.ts';
import { Badge, Btn, Card, Input, Modal, PageTitle, SectionLabel, TextArea } from '../../admin/ui.tsx';
import {
  dateLongue,
  euros,
  heure,
  nomDe,
  type Carte,
  type DescriptionFormule,
  type Evaluation,
  type Piste,
  type QuestionVendeur,
  type Reputation,
  type TableauCloser,
} from './types.ts';

type Onglet = 'pistes' | 'closer' | 'vendeur';

export function MisesEnRelation() {
  const [onglet, setOnglet] = useState<Onglet>('pistes');
  const [pistes, setPistes] = useState<{ formule: string | null; pistes: Piste[] } | null>(null);
  const [closer, setCloser] = useState<TableauCloser | null>(null);
  const [introductions, setIntroductions] = useState<Piste[] | null>(null);
  const [proposer, setProposer] = useState(false);

  const charger = useCallback(async () => {
    const [p, c, i] = await Promise.all([
      appelerApi<{ formule: string | null; pistes: Piste[] }>('/api/mise-en-relation/pistes'),
      appelerApi<TableauCloser>('/api/mise-en-relation/moi'),
      appelerApi<{ introductions: Piste[] }>('/api/mise-en-relation/introductions'),
    ]);
    setPistes(p.donnees);
    setCloser(c.donnees);
    setIntroductions(i.donnees?.introductions ?? []);
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const aDecider = (pistes?.pistes ?? []).filter((p) => p.statut === 'proposee').length;

  return (
    <>
      <PageTitle
        title={
          <>
            Mises en <em className="text-match-gradient font-serif italic font-medium">relation</em>
          </>
        }
        subtitle="Présenter deux entreprises qui avaient tout à gagner à se connaître est un métier. Ici, il se mesure et il se rémunère."
      />

      <Formation />

      <div className="mb-6 flex flex-wrap gap-2">
        <Btn variant={onglet === 'pistes' ? 'primary' : 'ghost'} onClick={() => setOnglet('pistes')}>
          Pour moi{aDecider > 0 ? ` · ${aDecider}` : ''}
        </Btn>
        <Btn variant={onglet === 'closer' ? 'primary' : 'ghost'} onClick={() => setOnglet('closer')}>
          Je présente
        </Btn>
        <Btn variant={onglet === 'vendeur' ? 'primary' : 'ghost'} onClick={() => setOnglet('vendeur')}>
          On me présente
        </Btn>
      </div>

      {onglet === 'pistes' && <MesPistes etat={pistes} onChange={charger} />}
      {onglet === 'closer' && <CoteCloser tableau={closer} onProposer={() => setProposer(true)} />}
      {onglet === 'vendeur' && <CoteVendeur introductions={introductions} onChange={charger} />}

      {proposer && (
        <AssistantProposition
          onClose={() => setProposer(false)}
          onFini={() => {
            setProposer(false);
            void charger();
          }}
        />
      )}
    </>
  );
}

function Formation() {
  return (
    <a href="https://croissance.ai" target="_blank" rel="noreferrer" className="group mb-6 block">
      <Card className="transition-colors duration-200 group-hover:bg-[#262626]">
        <div className="flex flex-wrap items-start gap-4">
          <GraduationCap size={20} strokeWidth={1.5} className="mt-0.5 shrink-0 text-fumee" />
          <div className="min-w-0 flex-1">
            <p className="text-[16px] text-blanc">Croissance.ai — Devenir un bon réseauteur</p>
            <p className="mt-1.5 max-w-[640px] text-[14px] leading-relaxed text-fumee">
              Poser les questions qui font parler un dirigeant, repérer l’intérêt commun avant les
              autres, chiffrer ce que deux entreprises gagneraient à se rencontrer, et présenter
              sans se griller. La formation qui transforme un carnet d’adresses en métier.
            </p>
            <span className="mt-3 inline-flex items-center gap-1 text-[13px] text-blanc">
              Découvrir la formation <ArrowUpRight size={13} strokeWidth={1.5} />
            </span>
          </div>
        </div>
      </Card>
    </a>
  );
}

/* ————————————————— Côté acheteur ————————————————— */

function MesPistes({ etat, onChange }: { etat: { formule: string | null; pistes: Piste[] } | null; onChange: () => Promise<void> }) {
  if (!etat) return <p className="text-[14px] text-fumee">Chargement…</p>;
  if (etat.pistes.length === 0) {
    return (
      <Card>
        <p className="text-[15px] text-blanc">Aucune piste pour l’instant.</p>
        <p className="mt-2 max-w-[620px] text-[14px] leading-relaxed text-fumee">
          Quand quelqu’un de votre réseau voit une occasion pour vous, elle arrive ici : le
          service, ce que ça pourrait vous rapporter, et pourquoi — avant même de savoir de qui il
          s’agit. Plus votre profil est précis, plus on pense à vous.
        </p>
      </Card>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {etat.pistes.map((p) => (
        <CartePiste key={p.id} piste={p} onChange={onChange} />
      ))}
    </div>
  );
}

function CartePiste({ piste, onChange }: { piste: Piste; onChange: () => Promise<void> }) {
  const [note, setNote] = useState<number | null>(piste.note_piste);
  const [erreur, setErreur] = useState<string | null>(null);
  const [formules, setFormules] = useState<DescriptionFormule[] | null>(null);
  const verrouillee = piste.statut === 'proposee';

  const debloquer = async () => {
    setErreur(null);
    const r = await envoyer(`/api/mise-en-relation/pistes/${piste.id}/debloquer`, { notePiste: note });
    if (r.erreur) {
      setErreur(r.erreur);
      const f = await appelerApi<{ formules: DescriptionFormule[] }>('/api/preenregistrement/formules');
      setFormules(f.donnees?.formules ?? []);
      return;
    }
    await onChange();
  };

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <SectionLabel>{verrouillee ? 'Piste à découvrir' : 'Piste débloquée'}</SectionLabel>
          <p className="mt-2 text-[17px] text-blanc">{piste.service}</p>
        </div>
        <div className="text-right">
          <p className="label-caps text-fumee">Gain estimé sur un an</p>
          <p className="text-match-gradient font-serif text-[28px] leading-tight">{euros(piste.gain_estime_centimes)}</p>
        </div>
      </div>

      <p className="mt-3 max-w-[720px] text-[14px] leading-relaxed text-fumee">{piste.explication}</p>

      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-fumee">
        {piste.vendeur_secteur && (
          <span className="flex items-center gap-1.5">
            <Building2 size={13} strokeWidth={1.5} /> {piste.vendeur_secteur}
          </span>
        )}
        {piste.vendeur_taille && <span>{piste.vendeur_taille} personne(s)</span>}
        {piste.closer && (
          <span>
            Présenté par <span className="text-blanc">{nomDe(piste.closer)}</span>
            {piste.closer.entreprise ? ` · ${piste.closer.entreprise}` : ''}
            <NoteCloser reputation={piste.closer.reputation} />
          </span>
        )}
      </div>

      {verrouillee && (
        <div className="mt-5 border-t border-graphite pt-5">
          <p className="text-[14px] text-blanc">Avant d’ouvrir : cette piste vous paraît bonne sur 10 ?</p>
          <p className="mt-1 text-[13px] text-fumee">
            Votre note juge le travail de celui qui vous présente, pas encore le résultat.
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                onClick={() => setNote(n)}
                className={`h-9 w-9 rounded-full border border-graphite text-[13px] transition-colors ${
                  note === n ? 'bg-blanc text-void' : 'text-fumee hover:text-blanc'
                }`}
              >
                {n}
              </button>
            ))}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Btn variant="primary" onClick={() => void debloquer()}>
              <Lock size={13} strokeWidth={1.5} className="mr-1.5 inline" />
              Découvrir l’entreprise
            </Btn>
            <Btn onClick={() => void envoyer(`/api/mise-en-relation/pistes/${piste.id}/refuser`, {}).then(onChange)}>
              Ce n’est pas pour moi
            </Btn>
            <span className="text-[12px] text-fumee">
              {piste.formule_requise === 'premium' ? 'Premium' : 'Pro'} — ou votre match offert du mois
            </span>
          </div>

          {erreur && <p className="mt-3 text-[13px]" style={{ color: 'var(--em-erreur)' }}>{erreur}</p>}
          {formules && <Formules formules={formules} requise={piste.formule_requise} />}
        </div>
      )}

      {!verrouillee && piste.vendeur && <Devoilee piste={piste} vendeur={piste.vendeur} onChange={onChange} />}
    </Card>
  );
}

function NoteCloser({ reputation }: { reputation: Reputation }) {
  if (reputation.note === null) return <span className="ml-1.5 text-fumee/70">· première mise en relation</span>;
  return (
    <span className="ml-1.5 inline-flex items-center gap-1 text-blanc">
      <Star size={11} strokeWidth={1.5} /> {reputation.note}/100
      <span className="text-fumee/70">({reputation.total})</span>
    </span>
  );
}

function Devoilee({ piste, vendeur, onChange }: { piste: Piste; vendeur: NonNullable<Piste['vendeur']>; onChange: () => Promise<void> }) {
  const [creneaux, setCreneaux] = useState<string[]>(['']);
  const [evaluation, setEvaluation] = useState<Evaluation | null>(piste.evaluation);
  const [commentaire, setCommentaire] = useState('');

  return (
    <div className="mt-5 border-t border-graphite pt-5">
      <div className="flex flex-wrap items-start gap-4">
        {vendeur.logo_url && <img src={vendeur.logo_url} alt="" className="h-12 w-12 rounded-[8px] object-contain" />}
        <div className="min-w-0 flex-1">
          <p className="text-[16px] text-blanc">{vendeur.entreprise ?? nomDe(vendeur)}</p>
          <p className="mt-0.5 text-[13px] text-fumee">
            {[nomDe(vendeur), vendeur.fonction, vendeur.secteur].filter(Boolean).join(' · ')}
          </p>
          {(vendeur.pitch || vendeur.services) && (
            <p className="mt-2 max-w-[680px] text-[14px] leading-relaxed text-fumee">{vendeur.pitch ?? vendeur.services}</p>
          )}
          <div className="mt-3 flex flex-wrap gap-4 text-[13px]">
            {vendeur.site_web && (
              <a href={vendeur.site_web} target="_blank" rel="noreferrer" className="text-blanc underline">
                Son site
              </a>
            )}
            {vendeur.email && <a href={`mailto:${vendeur.email}`} className="text-blanc underline">{vendeur.email}</a>}
            {vendeur.telephone && <span className="text-fumee">{vendeur.telephone}</span>}
          </div>
        </div>
      </div>

      {/* Le rendez-vous */}
      {piste.rendez_vous_le ? (
        <p className="mt-5 flex items-center gap-2 text-[14px] text-blanc">
          <Calendar size={14} strokeWidth={1.5} />
          Rendez-vous le {dateLongue(piste.rendez_vous_le)} à {heure(piste.rendez_vous_le)}
        </p>
      ) : (
        <div className="mt-5">
          <SectionLabel>Proposer un rendez-vous</SectionLabel>
          <p className="mt-1.5 text-[13px] text-fumee">
            Jusqu’à trois créneaux ; il en retiendra un.
            {piste.creneaux_proposes.length > 0 && ' Vous en avez déjà proposé, en attente de sa réponse.'}
          </p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            {creneaux.map((c, i) => (
              <Input
                key={i}
                label={i === 0 ? 'Créneau' : ''}
                value={c}
                onChange={(v) => setCreneaux(creneaux.map((x, j) => (j === i ? v : x)))}
                type="datetime-local"
              />
            ))}
            {creneaux.length < 3 && <Btn onClick={() => setCreneaux([...creneaux, ''])}>Ajouter</Btn>}
            <Btn
              variant="primary"
              onClick={() =>
                void envoyer(`/api/mise-en-relation/pistes/${piste.id}/creneaux`, {
                  creneaux: creneaux.filter(Boolean).map((c) => new Date(c).toISOString()),
                }).then(onChange)
              }
            >
              Proposer
            </Btn>
          </div>
        </div>
      )}

      {/* L'évaluation */}
      <div className="mt-6 border-t border-graphite pt-5">
        <SectionLabel>Après la rencontre</SectionLabel>
        {piste.evaluation ? (
          <p className="mt-2 text-[14px] text-blanc">
            Vous avez évalué cette mise en relation : <Badge filled>{LIBELLES_EVAL[piste.evaluation]}</Badge>
          </p>
        ) : (
          <>
            <p className="mt-1.5 text-[13px] text-fumee">
              Votre retour fait la réputation de celui qui vous a présenté — et décide de ce qu’on
              vous proposera ensuite.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {(Object.keys(LIBELLES_EVAL) as Evaluation[]).map((e) => (
                <Btn key={e} variant={evaluation === e ? 'primary' : 'ghost'} onClick={() => setEvaluation(e)}>
                  {LIBELLES_EVAL[e]}
                </Btn>
              ))}
            </div>
            {evaluation && (
              <div className="mt-3 max-w-[560px]">
                <TextArea label="Un mot (facultatif)" value={commentaire} onChange={setCommentaire} rows={2} />
                <Btn
                  variant="primary"
                  className="mt-3"
                  onClick={() =>
                    void envoyer(`/api/mise-en-relation/pistes/${piste.id}/evaluer`, {
                      evaluation,
                      commentaire: commentaire || null,
                    }).then(onChange)
                  }
                >
                  Envoyer mon évaluation
                </Btn>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const LIBELLES_EVAL: Record<Evaluation, string> = {
  no_match: 'No match',
  piste_faible: 'Piste faible',
  bonne_piste: 'Bonne piste',
  perfect_match: 'Perfect match',
};

function Formules({ formules, requise }: { formules: DescriptionFormule[]; requise: string }) {
  return (
    <div className="mt-4 grid gap-3 lg:grid-cols-3">
      {formules.map((f) => (
        <div
          key={f.cle}
          className={`rounded-[10px] border p-4 ${f.cle === requise ? 'border-blanc' : 'border-graphite'}`}
        >
          <p className="text-[15px] text-blanc">{f.nom}</p>
          <p className="text-match-gradient mt-1 font-serif text-[24px] leading-tight">
            {euros(f.prixCentimes)}
            <span className="font-sans text-[13px] text-fumee"> /mois</span>
          </p>
          <p className="mt-2 text-[13px] leading-relaxed text-fumee">{f.promesse}</p>
          <Btn
            variant={f.cle === requise ? 'primary' : 'ghost'}
            className="mt-3"
            onClick={() =>
              void envoyer<{ url: string | null }>('/api/preenregistrement/formule', { formule: f.cle }).then((r) => {
                if (r.donnees?.url) location.assign(r.donnees.url);
                else location.reload();
              })
            }
          >
            Choisir {f.nom}
          </Btn>
        </div>
      ))}
    </div>
  );
}

/* ————————————————— Côté closer ————————————————— */

function CoteCloser({ tableau, onProposer }: { tableau: TableauCloser | null; onProposer: () => void }) {
  if (!tableau) return <p className="text-[14px] text-fumee">Chargement…</p>;

  return (
    <>
      <Card className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex flex-wrap gap-8">
            <Compteur label="Présentations" valeur={tableau.proposees} />
            <Compteur label="Débloquées" valeur={tableau.debloquees} />
            <Compteur label="Rendez-vous" valeur={tableau.rendez_vous} />
            <div>
              <p className="label-caps text-fumee">Réputation</p>
              <p className="mt-1 text-[24px] font-light text-blanc">
                {tableau.reputation.note === null ? '—' : `${tableau.reputation.note}/100`}
              </p>
            </div>
          </div>
          <Btn variant="primary" onClick={onProposer}>
            Proposer une mise en relation
          </Btn>
        </div>
        <p className="mt-4 max-w-[700px] text-[13px] leading-relaxed text-fumee">
          Vous touchez <span className="text-blanc">1 %</span> de l’abonnement de chaque entreprise
          qui s’abonne pour débloquer vos mises en relation, tous les mois, tant qu’elle reste abonnée.
          Votre réputation dépend de la qualité de vos pistes, pas de leur nombre.
        </p>
      </Card>

      {tableau.propositions.length === 0 ? (
        <Card>
          <p className="text-[15px] text-blanc">Vous n’avez encore présenté personne.</p>
          <p className="mt-2 max-w-[620px] text-[14px] leading-relaxed text-fumee">
            Cherchez dans votre réseau deux entreprises qui gagneraient à se parler, et dites en
            chiffres ce que la rencontre leur rapporterait. L’assistant vous guide pour poser les
            bonnes questions.
          </p>
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          {tableau.propositions.map((p) => (
            <Card key={p.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[16px] text-blanc">{p.service}</p>
                  <p className="mt-1 text-[13px] text-fumee">
                    {p.vendeur?.entreprise ?? 'Vendeur'} → {p.acheteur?.entreprise ?? 'Acheteur'}
                  </p>
                </div>
                <div className="text-right">
                  <Badge filled={p.statut === 'evaluee' || p.statut === 'rendez_vous'}>{LIBELLES_STATUT[p.statut]}</Badge>
                  <p className="mt-2 text-[14px] text-blanc">{euros(p.gain_estime_centimes)}</p>
                </div>
              </div>
              {p.statut === 'brouillon' && (
                <p className="mt-3 text-[13px] text-fumee">Brouillon — il reste à l’envoyer.</p>
              )}
              {p.note_piste && (
                <p className="mt-3 text-[13px] text-fumee">
                  Noté <span className="text-blanc">{p.note_piste}/10</span> avant la rencontre.
                </p>
              )}
              {p.evaluation && (
                <p className="mt-2 text-[13px] text-fumee">
                  Après la rencontre : <span className="text-blanc">{LIBELLES_EVAL[p.evaluation]}</span>
                  {p.commentaire ? ` — « ${p.commentaire} »` : ''}
                </p>
              )}
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

const LIBELLES_STATUT: Record<string, string> = {
  brouillon: 'Brouillon',
  proposee: 'En attente',
  debloquee: 'Débloquée',
  rendez_vous: 'Rendez-vous fixé',
  evaluee: 'Évaluée',
  refusee: 'Déclinée',
  expiree: 'Expirée',
};

function Compteur({ label, valeur }: { label: string; valeur: number }) {
  return (
    <div>
      <p className="label-caps text-fumee">{label}</p>
      <p className="mt-1 text-[24px] font-light text-blanc">{valeur}</p>
    </div>
  );
}

/* ————————————————— L'assistant de proposition ————————————————— */

function AssistantProposition({ onClose, onFini }: { onClose: () => void; onFini: () => void }) {
  const [etape, setEtape] = useState<1 | 2 | 3>(1);
  const [vendeur, setVendeur] = useState<Carte | null>(null);
  const [acheteur, setAcheteur] = useState<Carte | null>(null);
  const [service, setService] = useState('');
  const [id, setId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<QuestionVendeur[]>([]);
  const [reponses, setReponses] = useState<Record<string, string>>({});
  const [gain, setGain] = useState('');
  const [explication, setExplication] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const demarrer = async () => {
    if (!vendeur || !acheteur || service.trim().length < 3) {
      setErreur('Choisissez les deux entreprises et nommez le service.');
      return;
    }
    setOccupe(true);
    setErreur(null);
    const r = await envoyer<{ id: string }>('/api/mise-en-relation/propositions', {
      vendeurId: vendeur.personne_id,
      acheteurId: acheteur.personne_id,
      service: service.trim(),
    });
    if (!r.donnees) {
      setOccupe(false);
      setErreur(r.erreur ?? 'Impossible de créer la proposition');
      return;
    }
    setId(r.donnees.id);
    const q = await appelerApi<{ questions: QuestionVendeur[] }>(
      `/api/mise-en-relation/propositions/${r.donnees.id}/questions`,
    );
    setQuestions(q.donnees?.questions ?? []);
    setOccupe(false);
    setEtape(2);
  };

  const chiffrer = async () => {
    if (!id) return;
    setOccupe(true);
    const r = await envoyer<{ gain_estime_centimes: number | null; explication: string | null }>(
      `/api/mise-en-relation/propositions/${id}/chiffrer`,
      {
        reponses: questions.map((q) => ({ cle: q.cle, question: q.texte, reponse: reponses[q.cle] ?? '' })),
      },
    );
    if (r.donnees?.gain_estime_centimes !== null && r.donnees?.gain_estime_centimes !== undefined) {
      setGain(Math.round(r.donnees.gain_estime_centimes / 100).toString());
    }
    if (r.donnees?.explication) setExplication(r.donnees.explication);
    setOccupe(false);
    setEtape(3);
  };

  const envoyerProposition = async () => {
    if (!id) return;
    setOccupe(true);
    setErreur(null);
    const r = await envoyer(`/api/mise-en-relation/propositions/${id}/envoyer`, {
      gainEstimeCentimes: Math.max(0, Math.round(Number(gain) || 0) * 100),
      explication: explication.trim(),
    });
    setOccupe(false);
    if (r.erreur) {
      setErreur(r.erreur);
      return;
    }
    onFini();
  };

  return (
    <Modal title={`Proposer une mise en relation · étape ${etape} sur 3`} onClose={onClose}>
      {etape === 1 && (
        <div className="flex flex-col gap-5">
          <ChoixPersonne label="Qui propose le service ?" choisi={vendeur} onChoisir={setVendeur} />
          <ChoixPersonne label="À qui cela pourrait-il servir ?" choisi={acheteur} onChoisir={setAcheteur} />
          <Input label="Le service, en une ligne" value={service} onChange={setService} placeholder="Automatisation de la saisie des commandes" />
          <p className="text-[13px] leading-relaxed text-fumee">
            L’étape suivante vous donne les questions à poser au vendeur pour chiffrer ce que
            l’acheteur y gagnerait. C’est ce chiffre qui fait la différence entre une mise en
            relation et une carte de visite.
          </p>
          {erreur && <p className="text-[13px]" style={{ color: 'var(--em-erreur)' }}>{erreur}</p>}
          <Btn variant="primary" onClick={() => void demarrer()} disabled={occupe}>
            {occupe ? 'Un instant…' : 'Continuer'}
          </Btn>
        </div>
      )}

      {etape === 2 && (
        <div className="flex flex-col gap-5">
          <p className="text-[14px] leading-relaxed text-fumee">
            Posez ces questions au vendeur, et notez ses réponses. Répondez à ce que vous savez —
            les vides ne bloquent rien.
          </p>
          {questions.map((q) => (
            <div key={q.cle}>
              <p className="text-[14px] text-blanc">{q.texte}</p>
              <p className="mt-1 text-[12px] leading-relaxed text-fumee">{q.aide}</p>
              <TextArea
                value={reponses[q.cle] ?? ''}
                onChange={(v) => setReponses({ ...reponses, [q.cle]: v })}
                rows={2}
              />
            </div>
          ))}
          <Btn variant="primary" onClick={() => void chiffrer()} disabled={occupe}>
            {occupe ? 'Estimation en cours…' : 'Proposer une estimation'}
          </Btn>
        </div>
      )}

      {etape === 3 && (
        <div className="flex flex-col gap-5">
          <p className="text-[14px] leading-relaxed text-fumee">
            Corrigez librement : c’est vous qui présentez, et c’est votre nom qui apparaît en face
            de ce chiffre. Une fourchette basse assumée vaut mieux qu’une promesse.
          </p>
          <Input label="Gain estimé sur un an (€)" value={gain} onChange={setGain} placeholder="12000" type="number" />
          <TextArea
            label="Pourquoi cette rencontre a du sens"
            value={explication}
            onChange={setExplication}
            rows={5}
            placeholder="Trois phrases, adressées à l’acheteur, sans nommer le fournisseur."
          />
          <p className="text-[13px] text-fumee">
            Au-delà de 25 000 €, la piste demandera la formule Premium à celui qui la reçoit (chacun a un match offert par mois).
          </p>
          {erreur && <p className="text-[13px]" style={{ color: 'var(--em-erreur)' }}>{erreur}</p>}
          <Btn variant="primary" onClick={() => void envoyerProposition()} disabled={occupe}>
            {occupe ? 'Envoi…' : 'Envoyer la proposition'}
          </Btn>
        </div>
      )}
    </Modal>
  );
}

function ChoixPersonne({
  label,
  choisi,
  onChoisir,
}: {
  label: string;
  choisi: Carte | null;
  onChoisir: (c: Carte | null) => void;
}) {
  const [q, setQ] = useState('');
  const [resultats, setResultats] = useState<Carte[]>([]);

  useEffect(() => {
    if (choisi || q.trim().length < 2) {
      setResultats([]);
      return;
    }
    const minuterie = setTimeout(async () => {
      const r = await appelerApi<{ profils: Carte[] }>(`/api/compte/annuaire?q=${encodeURIComponent(q.trim())}`);
      setResultats((r.donnees?.profils ?? []).slice(0, 6));
    }, 250);
    return () => clearTimeout(minuterie);
  }, [q, choisi]);

  if (choisi) {
    return (
      <div>
        <span className="label-caps block text-fumee">{label}</span>
        <div className="mt-2 flex items-center justify-between gap-3 rounded-[8px] border border-graphite px-3 py-2">
          <span className="truncate text-[14px] text-blanc">
            {choisi.entreprise ?? nomDe(choisi)}
            <span className="ml-2 text-[12px] text-fumee">{choisi.secteur}</span>
          </span>
          <button onClick={() => onChoisir(null)} aria-label="Changer" className="shrink-0 text-fumee hover:text-blanc">
            <X size={15} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Input label={label} value={q} onChange={setQ} placeholder="Nom d’entreprise ou de personne" />
      {resultats.length > 0 && (
        <div className="mt-2 flex flex-col gap-1">
          {resultats.map((r) => (
            <button
              key={r.personne_id}
              onClick={() => onChoisir(r)}
              className="flex items-center justify-between gap-3 rounded-[8px] border border-graphite px-3 py-2 text-left transition-colors hover:bg-[#262626]"
            >
              <span className="min-w-0 truncate text-[14px] text-blanc">
                {r.entreprise ?? nomDe(r)}
                <span className="ml-2 text-[12px] text-fumee">{r.secteur ?? ''}</span>
              </span>
              <Check size={14} strokeWidth={1.5} className="shrink-0 text-fumee" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ————————————————— Côté vendeur ————————————————— */

function CoteVendeur({ introductions, onChange }: { introductions: Piste[] | null; onChange: () => Promise<void> }) {
  if (!introductions) return <p className="text-[14px] text-fumee">Chargement…</p>;
  if (introductions.length === 0) {
    return (
      <Card>
        <p className="text-[15px] text-blanc">Personne ne vous a encore présenté.</p>
        <p className="mt-2 max-w-[620px] text-[14px] leading-relaxed text-fumee">
          Pour qu’on pense à vous, il faut qu’on sache précisément ce que vous faites et pour qui.
          Remplissez vos services et votre client idéal dans « Moi et mon entreprise » — c’est là
          que les closers vont chercher.
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {introductions.map((i) => (
        <Card key={i.id}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[16px] text-blanc">{i.service}</p>
              <p className="mt-1 text-[13px] text-fumee">
                Présenté par {i.closer ? nomDe(i.closer) : 'un membre du réseau'}
                {i.acheteur ? ` à ${i.acheteur.entreprise ?? nomDe(i.acheteur)}` : ' · l’entreprise reste anonyme tant qu’elle n’a pas ouvert'}
              </p>
            </div>
            <Badge filled={i.statut !== 'proposee'}>{LIBELLES_STATUT[i.statut]}</Badge>
          </div>

          {i.rendez_vous_le && (
            <p className="mt-3 flex items-center gap-2 text-[14px] text-blanc">
              <Calendar size={14} strokeWidth={1.5} />
              Rendez-vous le {dateLongue(i.rendez_vous_le)} à {heure(i.rendez_vous_le)}
            </p>
          )}

          {!i.rendez_vous_le && i.creneaux_proposes.length > 0 && (
            <div className="mt-4">
              <SectionLabel>Créneaux proposés</SectionLabel>
              <div className="mt-2 flex flex-wrap gap-2">
                {i.creneaux_proposes.map((c) => (
                  <Btn
                    key={c}
                    onClick={() =>
                      void envoyer(`/api/mise-en-relation/introductions/${i.id}/creneau`, { quand: c }).then(onChange)
                    }
                  >
                    {dateLongue(c)} · {heure(c)}
                  </Btn>
                ))}
              </div>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}
